import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import LandingPage from "./pages/LandingPage";
import InfoShopPage from "./pages/InfoShopPage";
import SpecPage from "./pages/SpecPage";
import EnrichmentPage from "./pages/EnrichmentPage";
import DashboardPage from "./pages/DashboardPage";
import ProductSpecPage from "./pages/ProductSpecPage";
import LoginPage from "./pages/LoginPage";
import OnboardingPage from "./pages/admin/OnboardingPage";
import SubSuppliersAdminPage from "./pages/admin/SubSuppliersAdminPage";
import SupplierOnboardingPage from "./pages/admin/SupplierOnboardingPage";
import PunchOutSessionsAdminPage from "./pages/admin/PunchOutSessionsAdminPage";
import PunchOutEnablementPage from "./pages/admin/PunchOutEnablementPage";
import MockSubSupplierPage from "./pages/MockSubSupplierPage";
import MockOriginPage from "./pages/MockOriginPage";
import SSALayout from "./ssa/SSALayout";
import SSACatalogPage from "./ssa/pages/CatalogPage";
import SSACategoryHubPage from "./ssa/pages/CategoryHubPage";
import SSAServicePage from "./ssa/pages/ServicePage";
import SSABuyerRequestsPage from "./ssa/pages/BuyerRequestsPage";
import SSABuyerRequestPage from "./ssa/pages/BuyerRequestPage";
import SSAWorkbenchPage from "./ssa/pages/WorkbenchPage";
import SSAWorkbenchRequestPage from "./ssa/pages/WorkbenchRequestPage";
import SSAPartnerPage from "./ssa/pages/PartnerPage";
import SSAInsightsPage from "./ssa/pages/InsightsPage";
// Email Workbench is preserved on disk but currently disabled —
// see /app/backend/server.py for the re-enable checklist.
// import EmailComposePage from "./pages/admin/EmailComposePage";
import { LocaleProvider } from "./i18n/LocaleContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

function AuthGate({ children }) {
  const { user } = useAuth();
  // PunchOut buyers arrive with ?punchout_session=<sid> after their
  // origin ERP (Coupa / Ariba / Aerchain) POSTs a PunchOutSetupRequest
  // and we redirect them to /infoshop. They already authenticated on
  // their side; the POSR session IS the credential. Do NOT force them
  // through the Infosys BPM internal login gate.
  const punchoutSession = (() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("punchout_session");
  })();
  if (user === undefined) {
    // Boot check against /api/auth/me — fleeting splash so the page
    // never flashes either screen prematurely.
    return (
      <div
        data-testid="auth-boot-splash"
        className="min-h-screen w-full flex items-center justify-center"
        style={{ background: "linear-gradient(180deg, #0B1B2D 0%, #14304F 100%)" }}
      >
        <Loader2 className="animate-spin text-white/80" size={28} />
      </div>
    );
  }
  if (user === null && !punchoutSession) return <LoginPage />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LocaleProvider>
          <BrowserRouter>
            <AuthGate>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/infoshop" element={<InfoShopPage />} />
                <Route path="/spec" element={<SpecPage />} />
                <Route path="/infoshop/admin/enrichment" element={<EnrichmentPage />} />
                <Route path="/infoshop/admin/onboarding" element={<OnboardingPage />} />
                <Route path="/infoshop/admin/sub-suppliers" element={<SubSuppliersAdminPage />} />
                <Route path="/infoshop/admin/supplier-onboarding" element={<SupplierOnboardingPage />} />
                <Route path="/infoshop/admin/punchout-sessions" element={<PunchOutSessionsAdminPage />} />
                <Route path="/infoshop/admin/punchout-enablement" element={<PunchOutEnablementPage />} />
                <Route path="/infoshop/punchout/mock-supplier" element={<MockSubSupplierPage />} />
                <Route path="/punchout/test-origin" element={<MockOriginPage />} />
                {/* <Route path="/infoshop/admin/email" element={<EmailComposePage />} /> — disabled */}
                <Route path="/infoshop/dashboard" element={<DashboardPage />} />
                <Route path="/services" element={<SSALayout />}>
                  <Route index element={<SSACategoryHubPage />} />
                  <Route path="c/:cat" element={<SSACatalogPage />} />
                  <Route path="s/:sid" element={<SSAServicePage />} />
                  <Route path="dm/:sid" element={<SSAServicePage />} />
                  <Route path="requests" element={<SSABuyerRequestsPage />} />
                  <Route path="requests/:rid" element={<SSABuyerRequestPage />} />
                  <Route path="workbench" element={<SSAWorkbenchPage />} />
                  <Route path="workbench/:rid" element={<SSAWorkbenchRequestPage />} />
                  <Route path="partner/:pid" element={<SSAPartnerPage />} />
                  <Route path="insights" element={<SSAInsightsPage />} />
                </Route>
                <Route path="/infoshop/product/:pn/spec" element={<ProductSpecPage />} />
              </Routes>
            </AuthGate>
          </BrowserRouter>
        </LocaleProvider>
      </AuthProvider>
      <Toaster
        position="bottom-right"
        richColors
        toastOptions={{ style: { fontFamily: "IBM Plex Sans, sans-serif" } }}
      />
    </div>
  );
}

export default App;
